import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material'
import { selectUsuario } from 'features/users/stores/userSlice'
import { useContext, useState } from 'react'
import { useSelector } from 'react-redux'
import { CustomThemeContext } from 'src/common/providers/CustomThemeProvider'
import i18n from 'src/lang/idiomas'
import { markDocumentAsReaded } from '../libs/markDocumentAsReaded'
import { markNewsAsRead } from '../libs/markNewsAsRead'
import LockDocumentNotification from './LockDocumentNotification'

function stripHtml(html) {
  if (!html) return ''
  const div = document.createElement('div')
  div.innerHTML = html
  return div.textContent || div.innerText || ''
}

const TIPO_COLORS = {
  Aviso: 'warning',
  Noticia: 'info',
  Comunicado: 'secondary',
  Documentacion: 'error',
  Corporativo: 'error',
  Personal: 'error',
}

const BloqueantesListModal = ({ bloqueantes, onClose }) => {
  const { themeController } = useContext(CustomThemeContext)

  const user = useSelector(selectUsuario)
  const [pendientes, setPendientes] = useState(bloqueantes)
  const [selectedItem, setSelectedItem] = useState(null)
  const [loadingAll, setLoadingAll] = useState(false)

  function handleItemAccepted() {
    setPendientes((prev) => prev.filter((b) => b !== selectedItem))
    setSelectedItem(null)
  }

  async function handleAceptarTodo() {
    setLoadingAll(true)
    try {
      for (const item of pendientes) {
        if (item.tipo_documento === 'Comunicado' || item.tipo_documento === 'Noticia') {
          await markNewsAsRead(item.documento.id, item.tipo_documento, item.lectura)
        } else {
          await markDocumentAsReaded(item.documento.id, item.documento.tipo_doc, item.lectura)
        }
      }
    } catch (_err) {
      // continue even if some fail
    } finally {
      setLoadingAll(false)
      onClose()
    }
  }

  return (
    <>
      <Dialog open={true} maxWidth="md" fullWidth onClose={() => {}}>
        <DialogTitle>
          {i18n.t('docpdlec', { defaultValue: 'Documentos pendientes de leer' })}
        </DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            {i18n.t('titpdlec', {
              defaultValue:
                'Estimado {{usuario}}, para proseguir con la navegación debes leer los siguientes documentos pendientes.',
              usuario: user?.nombre,
            })}
          </Typography>
          {loadingAll && <LinearProgress sx={{ mb: 1 }} />}
          <List disablePadding>
            {pendientes.map((item, idx) => (
              <ListItem
                key={idx}
                divider
                sx={{ pr: 14 }}
                secondaryAction={
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setSelectedItem(item)}
                    disabled={loadingAll}
                  >
                    {i18n.t('veracept', {
                      defaultValue: 'Ver y aceptar',
                    })}
                  </Button>
                }
              >
                <ListItemText
                  primary={
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: 1,
                        maxWidth: '90%',
                      }}
                    >
                      <Chip
                        label={item.tipo_documento}
                        size="small"
                        color={TIPO_COLORS[item.tipo_documento] ?? 'default'}
                      />
                      <Typography variant="body2" sx={{ whiteSpace: 'normal' }} noWrap>
                        {item.documento?.titulo ||
                          item.documento?.descripcion ||
                          item.documento?.nombre_archivo ||
                          stripHtml(item.documento?.texto_aviso) ||
                          item.tipo_documento}
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
          {pendientes.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {i18n.t('toddocac', {
                defaultValue: 'Todos los documentos han sido aceptados.',
              })}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          {pendientes.length === 0 ? (
            <Button variant="contained" onClick={onClose}>
              {i18n.t('general.cerrar', {
                defaultValue: 'Cerrar',
              })}
            </Button>
          ) : themeController?.hide?.BloqueantesListModal?.aceptar_todo ? (
            <></>
          ) : (
            <Button variant="contained" onClick={handleAceptarTodo} disabled={loadingAll}>
              {i18n.t('general.aceptod', {
                defaultValue: 'Aceptar todo',
              })}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {selectedItem && (
        <Dialog open={true} maxWidth="md" fullWidth onClose={() => setSelectedItem(null)}>
          <LockDocumentNotification data={selectedItem} onAccepted={handleItemAccepted} />
          <DialogActions sx={{ pt: 0 }}>
            <Button onClick={() => setSelectedItem(null)}>
              {i18n.t('vollist', {
                defaultValue: 'Volver a la lista',
              })}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  )
}

export default BloqueantesListModal
